// Libellés français des compétences MÉTIER importées dans les workspaces des apps (competences.sh) :
// leur nom d'origine est un identifiant technique (souvent anglais) et leur description est écrite
// pour le modèle (noms d'outils, scripts, consignes). On les traduit À L'AFFICHAGE seulement, par le
// nom d'origine : le modèle garde le nom qu'il connaît, et les compétences qui se chargent l'une
// l'autre par leur nom continuent de se trouver. Une compétence absente de la table s'affiche telle
// quelle (cas des compétences créées par un membre, ou déjà nommées en français).
//
// La table est rangée par app (marque de la SPA, NEXT_PUBLIC_MARQUE) : une compétence d'une autre
// app, ou créée sous le même nom dans un autre workspace, n'emprunte jamais ces libellés. Dans une
// app, le nom est unique (contrainte workspaceId, name, status) : il désigne bien la compétence
// importée. Hors d'une SPA d'app (marque absente, côté serveur), aucun libellé métier ne s'applique.

export interface LibelleCompetenceMetier {
  nom: string;
  description: string;
}

export const LIBELLES_COMPETENCES_METIER: Record<
  string,
  Record<string, LibelleCompetenceMetier>
> = {
  electron: {
    "analyse-ventes": {
      nom: "Analyse des ventes",
      description:
        "Rapport commercial complet d'une société à partir de ses factures clients.",
    },
    "cctp-dpgf-compare": {
      nom: "Comparatif CCTP et DPGF",
      description:
        "Compare un CCTP et un DPGF lot par lot pour repérer les incohérences.",
    },
    "company-analysis": {
      nom: "Analyse d'entreprise",
      description:
        "Fiche prospect d'une entreprise avant une prise de contact : identité, signaux d'achat, angle d'approche.",
    },
    "diagnostic-financier": {
      nom: "Diagnostic financier",
      description:
        "Indicateurs financiers clés d'une société : EBE, CAF, fonds de roulement, BFR et trésorerie nette.",
    },
    "linkedin-post": {
      nom: "Post LinkedIn",
      description:
        "Propose des sujets et rédige un post LinkedIn dans votre ton, avec une idée de visuel. Rien n'est publié.",
    },
    "lumys-brand": {
      nom: "Charte Lumys",
      description:
        "Charte graphique de Lumys (couleurs, polices, logo, ton), appliquée aux documents produits.",
    },
    "lumys-daily-dashboard": {
      nom: "Tableau de bord du jour",
      description:
        "Les chiffres clés du jour du groupe en une page : trésorerie par société et points d'attention.",
    },
    "pennylane-a-payer": {
      nom: "Factures à payer",
      description:
        "Ce qu'une société doit à ses fournisseurs, ce qui est en retard et l'échéancier des règlements.",
    },
    "pennylane-bank-synthesis": {
      nom: "Synthèse bancaire",
      description:
        "Synthèse quotidienne des comptes bancaires de toutes les sociétés du groupe, avec les alertes.",
    },
    "pennylane-ca": {
      nom: "Chiffre d'affaires",
      description:
        "Le chiffre d'affaires facturé par une société sur une période, mois par mois.",
    },
    "pennylane-clients": {
      nom: "Principaux clients",
      description:
        "Classement des clients d'une société et dépendance aux plus gros d'entre eux.",
    },
    "pennylane-comparatif": {
      nom: "Comparatif des sociétés",
      description:
        "Un même indicateur comparé sur toutes les sociétés du groupe, dans un seul tableau.",
    },
    "pennylane-depenses": {
      nom: "Dépenses",
      description:
        "Où part l'argent d'une société : dépenses par fournisseur et tendance mois par mois.",
    },
    "pennylane-flux": {
      nom: "Évolution de la trésorerie",
      description:
        "Entrées, sorties et solde de trésorerie d'une société, mois par mois.",
    },
    "pennylane-impayes": {
      nom: "Impayés clients",
      description:
        "Qui doit de l'argent à une société, depuis combien de temps, et qui relancer en premier.",
    },
    "pennylane-poste-de-depense": {
      nom: "Poste de dépense",
      description:
        "Combien une société a dépensé sur un poste précis (carburant, assurance, sous-traitance…).",
    },
    "pennylane-resultat": {
      nom: "Résultat",
      description:
        "Compte de résultat simplifié d'une société : produits, charges, bénéfice ou perte.",
    },
    "pennylane-tva": {
      nom: "TVA",
      description:
        "TVA collectée, TVA déductible et solde à payer d'une société.",
    },
    "plan-analysis": {
      nom: "Analyse de plan",
      description:
        "Lit un plan AutoCAD (DWG, DXF ou PDF) : métré, cartouche et calques.",
    },
  },
  boosts: {
    "Sélection d'articles client": {
      nom: "Sélection d'articles client",
      description:
        "Prépare une sélection de produits aux prix d'un client, puis le PDF et un brouillon de mail.",
    },
    "Fiche client 360": {
      nom: "Fiche client 360",
      description:
        "Tout sur un client en une réponse : coordonnées, interlocuteurs, fabricants, accusés et Moovago.",
    },
    "Veille appels d'offres": {
      nom: "Veille appels d'offres",
      description:
        "Trouve et qualifie les appels d'offres publics de mobilier du BOAMP, avec date limite et lien du dossier.",
    },
  },
  pirouette: {
    Meeko: {
      nom: "Meeko",
      description:
        "Consulter Meeko avec le navigateur : effectifs, plannings, contrats et équipes des crèches.",
    },
    "Charte Pirouette": {
      nom: "Charte Pirouette",
      description:
        "Charte graphique de la micro-crèche, appliquée à tous les documents produits.",
    },
  },
};

/** Libellé métier d'une compétence de l'app affichée, par son nom d'origine. */
export function libelleCompetenceMetier(
  nom: string,
  marque: string | undefined = process.env.NEXT_PUBLIC_MARQUE
): LibelleCompetenceMetier | undefined {
  // Propriétés propres seulement : un nom comme « constructor » ne doit rien trouver.
  if (!marque || !Object.hasOwn(LIBELLES_COMPETENCES_METIER, marque)) {
    return undefined;
  }
  const table = LIBELLES_COMPETENCES_METIER[marque];
  return Object.hasOwn(table, nom) ? table[nom] : undefined;
}
